/**
 * useTemplates Hook
 *
 * Manages the list of user templates and template operations using the V2 API.
 * Templates are self-contained with inline section content and explicit permissions.
 */

import { useCallback, useMemo } from 'react';
import { useEventTracking } from '@services/statsig';
import type { ForkNoteTemplateRequest } from '@store/generated/templates';
import { useSnackbar } from 'notistack';

import {
  useAddTemplateMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useForkTemplateMutation,
  useGetTemplateByIdQuery,
  useGetTemplateLibraryQuery,
  useGetTemplatesQuery,
  useRemoveTemplateMutation,
  useReorderTemplatesMutation,
} from '../store/api';
import type { TemplateOrderItem, TemplateWithMeta } from '../types';
import { toTemplateWithMeta } from '../types';

interface UseTemplatesReturn {
  /** List of user templates with metadata */
  templates: TemplateWithMeta[];
  /** Whether templates are loading */
  isLoading: boolean;
  /** Whether templates fetch errored */
  isError: boolean;
  /** Currently selected template data (when templateId provided) */
  selectedTemplate: TemplateWithMeta | null;
  /** Whether selected template is loading */
  isTemplateLoading: boolean;
  /** Public templates from library */
  libraryTemplates: TemplateWithMeta[];
  /** Whether library is loading */
  isLibraryLoading: boolean;
  /** Create new template from scratch */
  createFromScratch: () => Promise<string | null>;
  /** Fork/duplicate a template */
  forkTemplate: (templateId: string, options?: ForkNoteTemplateRequest) => Promise<string | null>;
  /** Add a PUBLIC template to user's list (opt-in) */
  addTemplate: (templateId: string) => Promise<boolean>;
  /** Delete a USER template */
  deleteTemplate: (templateId: string) => Promise<boolean>;
  /** Remove a template from user's list (opt-out for PUBLIC) */
  removeTemplate: (templateId: string) => Promise<boolean>;
  /** Reorder templates (bulk update display_order) */
  reorderTemplates: (templateOrders: TemplateOrderItem[]) => Promise<boolean>;
  /** Pin a template to a specific position (0-4) */
  pinTemplate: (templateId: string, position: number) => Promise<boolean>;
  /** Unpin a template (remove from pinned list) */
  unpinTemplate: (templateId: string) => Promise<boolean>;
  /** Refetch templates list */
  refetch: () => void;
}

interface UseTemplatesOptions {
  /** Template ID to fetch details for */
  selectedTemplateId?: string | null;
  /** Skip fetching library templates */
  skipLibrary?: boolean;
}

export const useTemplates = (options: UseTemplatesOptions = {}): UseTemplatesReturn => {
  const { selectedTemplateId, skipLibrary = false } = options;
  const { template: templateMetrics } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch active templates list
  const {
    data: templatesData,
    isLoading,
    isError,
    refetch: refetchTemplates,
  } = useGetTemplatesQuery();

  // Wrap refetch to return void (RTK Query refetch returns a promise)
  const refetch = useCallback(() => {
    void refetchTemplates();
  }, [refetchTemplates]);

  // Fetch library templates (PUBLIC templates for discovery)
  const {
    data: libraryData,
    isLoading: isLibraryLoading,
  } = useGetTemplateLibraryQuery(undefined, { skip: skipLibrary });

  // Fetch selected template details
  const {
    data: selectedTemplateData,
    isLoading: isTemplateLoading,
  } = useGetTemplateByIdQuery(
    { templateId: selectedTemplateId || '' },
    { skip: !selectedTemplateId },
  );

  // Mutations
  const [createMutation] = useCreateTemplateMutation();
  const [forkMutation] = useForkTemplateMutation();
  const [addMutation] = useAddTemplateMutation();
  const [deleteMutation] = useDeleteTemplateMutation();
  const [removeMutation] = useRemoveTemplateMutation();
  const [reorderMutation] = useReorderTemplatesMutation();

  // Transform templates list to include UI metadata
  const templates = useMemo((): TemplateWithMeta[] => {
    if (!templatesData?.templates) return [];
    return templatesData.templates.map(t => toTemplateWithMeta(t));
  }, [templatesData]);

  // Transform library templates
  const libraryTemplates = useMemo((): TemplateWithMeta[] => {
    if (!libraryData?.templates) return [];
    return libraryData.templates.map(t => toTemplateWithMeta(t));
  }, [libraryData]);

  // Transform selected template
  const selectedTemplate = useMemo((): TemplateWithMeta | null => {
    if (!selectedTemplateData) return null;
    return toTemplateWithMeta(selectedTemplateData);
  }, [selectedTemplateData]);

  // Create from scratch
  const createFromScratch = useCallback(async (): Promise<string | null> => {
    try {
      const result = await createMutation({
        createNoteTemplateRequest: {
          title: '[Name Your Template]',
          description: null,
          prompt: null,
          sections: [],
        },
      }).unwrap();

      const newTemplateId = result.template.id;
      templateMetrics.customizationStarted(newTemplateId, {
        source: 'new',
        initial_sections_count: 0,
      });

      return newTemplateId;
    } catch (error) {
      console.error('Failed to create template:', error);
      enqueueSnackbar('Failed to create template', { variant: 'error' });
      return null;
    }
  }, [createMutation, templateMetrics, enqueueSnackbar]);

  // Fork/duplicate template
  // When options are provided (admin duplicate flow), the caller handles the success toast
  const forkTemplate = useCallback(async (templateId: string, options?: ForkNoteTemplateRequest): Promise<string | null> => {
    try {
      const result = await forkMutation({
        templateId,
        forkNoteTemplateRequest: options ?? {},
      }).unwrap();
      const newTemplateId = result.template.id;

      templateMetrics.customizationStarted(newTemplateId, {
        source: 'public_copy',
        initial_sections_count: result.revision.sections?.length || 0,
      });

      if (!options) {
        enqueueSnackbar('Template copied to your templates', { variant: 'success' });
      }
      return newTemplateId;
    } catch (error) {
      console.error('Failed to fork template:', error);
      enqueueSnackbar('Failed to copy template', { variant: 'error' });
      return null;
    }
  }, [forkMutation, templateMetrics, enqueueSnackbar]);

  // Add PUBLIC template to user's list (opt-in)
  const addTemplate = useCallback(async (templateId: string): Promise<boolean> => {
    try {
      await addMutation({ templateId }).unwrap();
      enqueueSnackbar('Template added to your list', { variant: 'success' });
      return true;
    } catch (error) {
      console.error('Failed to add template:', error);
      enqueueSnackbar('Failed to add template', { variant: 'error' });
      return false;
    }
  }, [addMutation, enqueueSnackbar]);

  // Delete USER template
  const deleteTemplate = useCallback(async (templateId: string): Promise<boolean> => {
    try {
      await deleteMutation({ templateId }).unwrap();
      templateMetrics.delete(templateId, { template_type: 'user' });
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      return true;
    } catch (error) {
      console.error('Failed to delete template:', error);
      enqueueSnackbar('Failed to delete template', { variant: 'error' });
      return false;
    }
  }, [deleteMutation, templateMetrics, enqueueSnackbar]);

  // Remove template from user's list (opt-out for PUBLIC)
  const removeTemplate = useCallback(async (templateId: string): Promise<boolean> => {
    try {
      await removeMutation({ templateId }).unwrap();
      templateMetrics.delete(templateId, { template_type: 'public' });
      enqueueSnackbar('Template removed from your list', { variant: 'success' });
      return true;
    } catch (error) {
      console.error('Failed to remove template:', error);
      enqueueSnackbar('Failed to remove template', { variant: 'error' });
      return false;
    }
  }, [removeMutation, templateMetrics, enqueueSnackbar]);

  // Reorder templates (bulk update display_order)
  const reorderTemplates = useCallback(
    async (templateOrders: TemplateOrderItem[]): Promise<boolean> => {
      try {
        await reorderMutation({
          reorderTemplatesRequest: {
            template_orders: templateOrders,
          },
        }).unwrap();

        // Track reorder event if we have position info
        if (templateOrders.length > 0) {
          const firstOrder = templateOrders[0];
          const pinnedCount = templates.filter(t => t.displayOrder !== null).length;
          templateMetrics.reorder(firstOrder.template_id, {
            pinned_count: pinnedCount,
          });
        }

        return true;
      } catch (error) {
        console.error('Failed to reorder templates:', error);
        enqueueSnackbar('Failed to reorder templates', { variant: 'error' });
        return false;
      }
    },
    [reorderMutation, templateMetrics, templates],
  );

  // Pin a template to a specific position (0-4)
  const pinTemplate = useCallback(
    async (templateId: string, position: number): Promise<boolean> => {
      try {
        // Get current pinned templates count
        const pinnedCount = templates.filter(t => t.displayOrder !== null).length;

        // Build template orders: update the pinned template and shift others if needed
        const templateOrders: TemplateOrderItem[] = [];

        // Add the template being pinned
        templateOrders.push({
          template_id: templateId,
          display_order: position,
        });

        // Shift existing pinned templates that are at or after this position
        templates.forEach(template => {
          if (template.displayOrder !== null && template.template.id !== templateId) {
            const currentOrder = template.displayOrder;
            if (currentOrder >= position) {
              // Shift down by 1, but cap at 4
              const newOrder = Math.min(currentOrder + 1, 4);
              templateOrders.push({
                template_id: template.template.id,
                display_order: newOrder,
              });
            } else {
              // Keep existing order
              templateOrders.push({
                template_id: template.template.id,
                display_order: currentOrder,
              });
            }
          }
        });

        await reorderMutation({
          reorderTemplatesRequest: {
            template_orders: templateOrders,
          },
        }).unwrap();

        templateMetrics.pin(templateId, {
          position,
          pinned_count: pinnedCount + 1,
        });

        enqueueSnackbar('Template pinned', { variant: 'success' });
        return true;
      } catch (error) {
        console.error('Failed to pin template:', error);
        enqueueSnackbar('Failed to pin template', { variant: 'error' });
        return false;
      }
    },
    [reorderMutation, templateMetrics, templates],
  );

  // Unpin a template (remove from pinned list)
  const unpinTemplate = useCallback(
    async (templateId: string): Promise<boolean> => {
      try {
        const pinnedCount = templates.filter(t => t.displayOrder !== null).length;
        const templateToUnpin = templates.find(t => t.template.id === templateId);
        const unpinPosition = templateToUnpin?.displayOrder ?? null;

        if (unpinPosition === null) {
          // Already unpinned
          return true;
        }

        // Build template orders: unpin this template and shift others up
        const templateOrders: TemplateOrderItem[] = [];

        // Unpin the template
        templateOrders.push({
          template_id: templateId,
          display_order: null,
        });

        // Shift existing pinned templates that are after this position
        templates.forEach(template => {
          if (
            template.displayOrder !== null &&
            template.template.id !== templateId &&
            template.displayOrder > unpinPosition
          ) {
            // Shift up by 1
            templateOrders.push({
              template_id: template.template.id,
              display_order: template.displayOrder - 1,
            });
          } else if (
            template.displayOrder !== null &&
            template.template.id !== templateId
          ) {
            // Keep existing order
            templateOrders.push({
              template_id: template.template.id,
              display_order: template.displayOrder,
            });
          }
        });

        await reorderMutation({
          reorderTemplatesRequest: {
            template_orders: templateOrders,
          },
        }).unwrap();

        templateMetrics.unpin(templateId, {
          pinned_count: pinnedCount - 1,
        });

        enqueueSnackbar('Template unpinned', { variant: 'success' });
        return true;
      } catch (error) {
        console.error('Failed to unpin template:', error);
        enqueueSnackbar('Failed to unpin template', { variant: 'error' });
        return false;
      }
    },
    [reorderMutation, templateMetrics, templates],
  );

  return {
    templates,
    isLoading,
    isError,
    selectedTemplate,
    isTemplateLoading,
    libraryTemplates,
    isLibraryLoading,
    createFromScratch,
    forkTemplate,
    addTemplate,
    deleteTemplate,
    removeTemplate,
    reorderTemplates,
    pinTemplate,
    unpinTemplate,
    refetch,
  };
};
